import { useEffect, useState } from "react"
import { ImageOff } from "lucide-react"

import { cn } from "@f2e/ui/lib/utils"

type ImageLoadState = "loading" | "ready" | "error"

export interface KnowledgeBaseImagePreviewProps {
  src: string | null
  alt: string
  className?: string
  imageClassName?: string
  compact?: boolean
  pending?: boolean
  loadingLabel: string
  errorLabel: string
}

export const KnowledgeBaseImagePreview = ({
  src,
  alt,
  className,
  imageClassName,
  compact = false,
  pending = false,
  loadingLabel,
  errorLabel,
}: KnowledgeBaseImagePreviewProps) => {
  const [loadState, setLoadState] = useState<ImageLoadState>(
    pending || src ? "loading" : "error"
  )

  useEffect(() => {
    setLoadState(pending || src ? "loading" : "error")
  }, [pending, src])

  return (
    <div
      className={cn(
        "relative flex overflow-hidden bg-slate-50 dark:bg-slate-950",
        className
      )}
      data-image-load-state={loadState}
    >
      {loadState === "loading" && (
        <div
          className="absolute inset-0 animate-pulse bg-slate-100 dark:bg-slate-900"
          role="status"
          aria-label={loadingLabel}
        />
      )}
      {loadState === "error" && (
        <div
          className={cn(
            "m-auto flex items-center justify-center text-slate-400 dark:text-slate-500",
            compact ? "h-8 w-8" : "flex-col gap-2 px-6 text-center text-sm"
          )}
          role="img"
          aria-label={errorLabel}
        >
          <ImageOff className={compact ? "h-5 w-5" : "h-8 w-8"} />
          {!compact && <span>{errorLabel}</span>}
        </div>
      )}
      {src && !pending && (
        <img
          src={src}
          alt={alt}
          loading={compact ? "lazy" : "eager"}
          decoding="async"
          className={cn(
            "h-full w-full object-contain transition-opacity duration-150",
            loadState === "ready" ? "opacity-100" : "opacity-0",
            imageClassName
          )}
          onLoad={() => setLoadState("ready")}
          onError={() => setLoadState("error")}
        />
      )}
    </div>
  )
}
